import { SkeletonLoader } from "./SkeletonLoader";
import { LoadingOutlet } from "../Dashboard/LoadingOutlet";
import { Spinner } from "../ui";

/*
 * What a code-split route shows while its code downloads on a direct load
 * (see `lazyPage` in main.tsx). React Router renders these as the route's
 * hydrateFallbackElement, only during that initial load, inside the layouts
 * above the route, which render for real. Navigations inside the app never
 * show them: the router fetches a route's code before it commits the
 * navigation to it.
 *
 * Each is shaped like what replaces it, because the swap is a layout change
 * the visitor sees and Core Web Vitals score.
 */

/**
 * For the pages inside HomeLayout (/dashboard, /upgrade, /privacy, ...).
 *
 * HomeLayout's footer is taken out of the layout while this is up. Wherever
 * it sat, the page arriving would move it — out of view under the several
 * screens of /privacy, into view above a short page like /status or the
 * signed-out /dashboard — and a visible element moving is a layout shift:
 * measured at phone size, 0.33 of CLS for /privacy and 0.17 for the short
 * pages, against 0.03 or less before the split. A footer that appears
 * together with the page it ends is not a shift at all.
 *
 * The spinner is the one the pages show while the account loads, in the same
 * spot, and fades in after a beat, so a chunk that arrives quickly — from
 * cache, or on a fast connection — swaps in without a flash.
 */
export function PageFallback() {
  return (
    <div data-route-fallback className="flex w-full justify-center py-24">
      <style>{"main:has([data-route-fallback]) + footer{display:none}"}</style>
      <div className="animate-[fadeIn_0.3s_ease-out_0.4s_both]">
        <Spinner size={32} />
      </div>
    </div>
  );
}

/**
 * For /dashboard/:guildId. SidebarLayout is the page chrome of every server
 * route and is itself code-split, so nothing around this renders the frame.
 * This mirrors SidebarLayout's own first render — it has no data yet either,
 * so that is a skeleton too: same sidebar, same main-area skeleton — and at
 * md widths and up the handover from one to the other changes nothing on
 * screen. Below md, SidebarLayout's fixed menu button appears at the handover;
 * it is position:fixed, so nothing moves.
 *
 * Keep it in step with SidebarLayout's loading branch.
 */
export function DashboardFallback() {
  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-900 to-gray-950 text-gray-100 overflow-hidden">
      {/* Off-canvas below md, as in SidebarLayout, so only desktop shows it. */}
      <aside className="hidden md:flex w-67 bg-surface-1 border-r border-gray-700 flex-col">
        <div className="p-4 border-b border-gray-700 min-h-[153px]">
          <SkeletonLoader />
          <div className="mt-4">
            <SkeletonLoader height="h-14" />
          </div>
        </div>
        <div className="flex-1 p-4 space-y-2">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-lg font-semibold text-white">RoleLogic</div>
            <div className="w-12 h-5 bg-gray-700 rounded-full animate-pulse" />
          </div>
          <div className="w-full h-10 bg-gray-700/50 rounded-md animate-pulse" />
        </div>
        <div className="p-4 border-t border-gray-700">
          <SkeletonLoader height="h-10" />
        </div>
      </aside>

      <div className="flex-1 h-full overflow-hidden bg-gray-900">
        <div className="h-full overflow-y-auto">
          <div className="p-4">
            <div className="h-10" />
          </div>
          <LoadingOutlet />
        </div>
      </div>
    </div>
  );
}
